import { Injectable } from '@angular/core';
import { AdministratorFacade, getRolesAndPermission } from '@piqet-angular/data';
import {
  dblclickTooltipDef,
  defaultColumn,
  DELETE_RECORD_LABEL,
  grid_widths_map,
  mainModuleName,
  rolesModuleName,
  subModuleName,
} from '@piqet-angular/shared';
import { right } from '@popperjs/core';
import { ColDef } from 'ag-grid-community';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class IngredientsDataGridService {
  event: any;
  ingridientsAdminPayload: any;

  editIngredients = new Subject<[any, any, any]>();
  editIngredientsData$ = this.editIngredients.asObservable();

  deleteIngredients = new Subject<[any]>();
  deleteIngredientsData$ = this.deleteIngredients.asObservable();

  constructor(public administratorFacade: AdministratorFacade, private swalService: SwalService) {}

  getAdministratorIngredientColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'IngredientId',
      headerName: 'IngredientId',
      field: 'IngredientId',
      hide: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'IngredientCategoryName',
      headerName: 'Ingredient category',
      field: 'ingredientCategoryName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
      filter: 'agTextColumnFilter',
      cellRenderer: (params: any) => {
        const cellValue = params.value;
        const cellElement = document.createElement('span');
        cellElement.innerHTML = `${cellValue}`;
        cellElement.style.textDecoration = 'underline';
        const showEdit = this.isAccess() ? this.isAccess().isEdit : true;
        if (showEdit) {
          cellElement.style.cursor = 'pointer';
        }
        cellElement.title = dblclickTooltipDef;
        cellElement.addEventListener('dblclick', () => {
          const showEdit = this.isAccess() ? this.isAccess().isEdit : true;
          showEdit &&
            this.editIngredients.next([
              params.data.ingredientId,
              params.data.regionId,
              'Review ingredient detail',
            ]);
        });
        return cellElement;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'IngredientCode',
      headerName: 'Ingredient code',
      field: 'ingredientCode',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
      filter: 'agTextColumnFilter',
    });

    colDef.push({
      ...defaultColumn,
      colId: 'IngredientName',
      headerName: 'Ingredient',
      field: 'ingredientName',
      cellClass: 'my-permission-cursor-pointer',
      filter: 'agTextColumnFilter',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'RegionCode',
      headerName: 'Region code',
      field: 'regionCode',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      lockVisible: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'RegionName',
      headerName: 'Region',
      field: 'regionName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      filter: 'agTextColumnFilter',
      autoHeight: true,
      resizable: false,
      lockVisible: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'OrganizationName',
      headerName: 'Organization',
      field: 'organizationName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      resizable: false,
      lockVisible: true,
      filter: 'agTextColumnFilter',
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRendererParams: {
        showEdit: this.isAccess() ? this.isAccess().isEdit : true,
        showDelete: this.isAccess() ? this.isAccess().isDelete : true,
        value: 'admin-ingredients',
        editData: (data: any) => {
          this.editIngredients.next([data.ingredientId, data.regionId, 'Review ingredient detail']);
        },
        deleteData: (data: any) => {
          this.deleteConfirmation(data);
        },
      },
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      lockVisible: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  isAccess = () => {
    return getRolesAndPermission(
      rolesModuleName.DataManageMaterialImpactIngredients,
      subModuleName.materialImpactDataModule
    );
  };

  getPayload(searchData: any, sortColumnState?: any, event?: any) {
    if (event) {
      this.event = event;
    }
    const currentPage = this.event?.goToPage
      ? this.event.goToPage
      : this.event?.next
      ? this.event.currentPage + 1
      : this.event?.prev
      ? this.event?.currentPage - 1
      : this.event?.gridApi?.paginationGetCurrentPage() + 1;
    const pageSize = this.event?.gridApi.paginationGetPageSize();
    const startCount = pageSize * (currentPage - 1);

    const columns = this.getAdministratorIngredientColumn()
      .map((item: any) => {
        return {
          searchable: true,
          name: item.colId,
          orderable: true,
          data: item.colId,
        };
      })
      .filter(value => value?.name != 'Actions');

    return (this.ingridientsAdminPayload = {
      search: {
        columnNameList: [],
        value: searchData || '',
      },
      columns: columns,
      order: [
        {
          columnName: sortColumnState?.columnName || 'IngredientId',
          dir:
            sortColumnState?.dir[0].toUpperCase() + sortColumnState?.dir.substr(1).toLowerCase() ||
            'Asc',
        },
      ],
      start: startCount || 0,
      length: pageSize == Number(this.event?.totalRecord) ? Number('-1') : pageSize || 10,
      draw: currentPage || 1,
    });
  }

  deleteConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deleteIngredients.next([data]);
      }
    });
  }
}
